import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import JobZImage from "../../../common/jobz-img";
import { useAuth } from "../../../../context/AuthContext";
import { jobService } from "../../../../services/job-service";
import { empRoute, employer, publicUser } from "../../../../globals/route-names";

const emptyForm = {
    id: "",
    name: "",
    website: "",
    location: "",
    description: ""
};

function EmpCompaniesPage() {
    const { user } = useAuth();
    const [companies, setCompanies] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [logoFile, setLogoFile] = useState(null);
    const [bannerFile, setBannerFile] = useState(null);

    const selectedCompany = useMemo(() => companies.find((company) => company.id === form.id), [companies, form.id]);

    const load = () => {
        setLoading(true);
        jobService.employerCompany()
            .then((data) => {
                const list = data.companies || (data.company ? [data.company] : []);
                setCompanies(list);
                if (!form.id && list.length > 0) selectCompany(list[0]);
            })
            .catch((error) => setMessage(error.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const selectCompany = (company) => {
        setForm({
            id: company.id || "",
            name: company.name || "",
            website: company.website || "",
            location: company.location || "",
            description: company.description || ""
        });
        setLogoFile(null);
        setBannerFile(null);
    };

    const addNew = () => {
        setForm({ ...emptyForm, name: `${user?.name || "Employer"}'s Company` });
        setLogoFile(null);
        setBannerFile(null);
        setMessage("Add a new company. It will be available for jobs after admin approval.");
    };

    const update = (event) => setForm({ ...form, [event.target.name]: event.target.value });

    const submit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setMessage("");
        try {
            const payload = {
                name: form.name,
                website: form.website,
                location: form.location,
                description: form.description
            };
            const data = form.id
                ? await jobService.updateEmployerCompany(form.id, payload)
                : await jobService.saveEmployerCompany(payload);
            const list = data.companies || [];
            const saved = form.id ? list.find((item) => item.id === form.id) : list[0];
            if (saved?.id && logoFile) await jobService.uploadEmployerCompanyLogo(saved.id, logoFile);
            if (saved?.id && bannerFile) await jobService.uploadEmployerCompanyBanner(saved.id, bannerFile);
            const fresh = await jobService.employerCompany();
            const freshList = fresh.companies || [];
            setCompanies(freshList);
            const next = freshList.find((item) => item.id === saved?.id) || freshList[0];
            if (next) selectCompany(next);
            setMessage("Company saved and sent for admin approval. Approved companies can be used for jobs.");
        } catch (error) {
            setMessage(error.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="wt-admin-right-page-header clearfix">
                <h2>Companies</h2>
                <div className="breadcrumbs"><NavLink to={publicUser.HOME1}>Home</NavLink><NavLink to={empRoute(employer.DASHBOARD)}>Dashboard</NavLink><span>Companies</span></div>
            </div>

            {message && <div className="alert alert-info">{message}</div>}

            <div className="panel panel-default">
                <div className="panel-heading wt-panel-heading p-a20 d-flex justify-content-between align-items-center">
                    <h4 className="panel-tittle m-a0"><i className="fa fa-building" />Company List</h4>
                    <button className="site-button" type="button" onClick={addNew}>Add Company</button>
                </div>
                <div className="panel-body wt-panel-body p-a20 m-b30">
                    {loading ? <div className="p-a30 text-center">Loading companies...</div> : (
                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead><tr><th>Company</th><th>Location</th><th>Status</th><th>Action</th></tr></thead>
                                <tbody>
                                    {companies.length === 0 ? (
                                        <tr><td colSpan="4" className="text-center">No companies added yet.</td></tr>
                                    ) : companies.map((company, index) => (
                                        <tr key={company.id}>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div style={{ width: 52, height: 52 }}><JobZImage src={company.logo_url || `images/jobs-company/pic${(index % 5) + 1}.jpg`} alt={company.name} /></div>
                                                    <div><strong>{company.name}</strong><div className="text-muted">{company.website}</div></div>
                                                </div>
                                            </td>
                                            <td>{company.location || ""}</td>
                                            <td><span className={`badge ${company.verified ? "bg-success" : "bg-warning text-dark"}`}>{company.verified ? "Approved" : "Pending Approval"}</span></td>
                                            <td><button className="btn btn-sm btn-outline-primary" type="button" onClick={() => selectCompany(company)}>Edit</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <div className="panel panel-default">
                <div className="panel-heading wt-panel-heading p-a20">
                    <h4 className="panel-tittle m-a0">{form.id ? "Edit Company" : "Add Company"}</h4>
                </div>
                <div className="panel-body wt-panel-body p-a20 m-b30">
                    {selectedCompany && (
                        <div className="row m-b20">
                            <div className="col-md-3"><JobZImage src={selectedCompany.logo_url || "images/jobs-company/pic1.jpg"} alt={selectedCompany.name} /></div>
                            <div className="col-md-9">
                                <p className="m-b5"><strong>Approval:</strong> {selectedCompany.verified ? "Approved" : "Pending admin approval"}</p>
                                {selectedCompany.banner_url && <JobZImage src={selectedCompany.banner_url} alt={`${selectedCompany.name} banner`} />}
                            </div>
                        </div>
                    )}
                    <form onSubmit={submit}>
                        <div className="row">
                            <Field label="Company Name" name="name" value={form.name} onChange={update} required />
                            <Field label="Website" name="website" value={form.website} onChange={update} placeholder="https://..." />
                            <Field label="Location" name="location" value={form.location} onChange={update} placeholder="City, Country" />
                            <FileField label="Company Logo" onChange={(event) => setLogoFile(event.target.files?.[0] || null)} />
                            <FileField label="Company Cover/Banner Image" onChange={(event) => setBannerFile(event.target.files?.[0] || null)} />
                            <div className="col-md-12">
                                <div className="form-group">
                                    <label>Description</label>
                                    <textarea className="form-control" name="description" rows={5} placeholder="Tell candidates about your company." value={form.description} onChange={update} />
                                </div>
                            </div>
                            <div className="col-lg-12 col-md-12">
                                <button type="submit" className="site-button" disabled={submitting}>{submitting ? "Saving..." : form.id ? "Save Company" : "Add Company"}</button>
                                {form.id && <button type="button" className="site-button-secondry m-l10" onClick={addNew}>Clear / Add New</button>}
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
}

function Field({ label, name, value, onChange, required = false, placeholder = "" }) {
    return (
        <div className="col-xl-4 col-lg-6 col-md-12">
            <div className="form-group">
                <label>{label}</label>
                <input className="form-control" name={name} type="text" required={required} placeholder={placeholder || label} value={value} onChange={onChange} />
            </div>
        </div>
    );
}

function FileField({ label, onChange }) {
    return (
        <div className="col-xl-6 col-lg-6 col-md-12">
            <div className="form-group">
                <label>{label}</label>
                <input className="form-control" type="file" accept="image/*" onChange={onChange} />
            </div>
        </div>
    );
}

export default EmpCompaniesPage;
